/**
 * useJourney Hook
 *
 * Provides journey generation capabilities to React components.
 *
 * Features:
 * - Staged progress tracking (not streaming - JSON needs complete response)
 * - Concurrent request prevention
 * - localStorage persistence
 * - Fallback handling
 * - Server-side rate limiting (email, IP, device)
 */

import { useState, useCallback, useRef } from 'react';
import type { JourneyOutput } from '@/lib/ai/schemas/journeyOutput.schema';
import type { FactPack } from '@/lib/ai/schemas/factPack.schema';
import type { ProgressStage } from '@/lib/ai/core/types';
import {
  saveJourney,
  loadJourney,
  shouldRegenerate,
  generateFallbackJourney,
} from '@/lib/ai/journey';
import { hashFactPackForCache } from '@/lib/utils/hash';
import { API_ENDPOINTS } from '@/lib/ai/core/config';
import { MOCK_JOURNEY_RESPONSE, USE_MOCK, USE_RULE_ENGINE_ONLY } from '@/lib/mocks/mockJourneyResponse';
import { getOrCreateDeviceId } from '@/lib/email/emailCapture';

// Re-export ProgressStage for convenience
export type { ProgressStage };

// ============================================
// Types
// ============================================

export interface JourneyProgress {
  stage: ProgressStage;
  message: string;
}

export interface UseJourneyOptions {
  factPack: FactPack;
  onSuccess?: (journey: JourneyOutput, remaining: number) => void;
  onError?: (error: string) => void;
}

export interface UseJourneyReturn {
  // State
  journey: JourneyOutput | null;
  isGenerating: boolean;
  error: string | null;
  progress: JourneyProgress;
  isFallback: boolean;

  // Data quality warnings from API (e.g., "riskLevel field defaulted")
  warnings: string[];

  // Pre-computed data from Rule Engine (for direct UI rendering)
  // This bypasses LLM and shows deterministic data
  preComputedData: FactPack['preComputedJourneyData'] | null;

  // Rate limit info (updated from server response)
  remainingGenerations: number;

  // Actions
  generate: (email: string) => Promise<void>;
  clear: () => void;
}

// Progress stage messages
const PROGRESS_MESSAGES: Record<ProgressStage, string> = {
  idle: '',
  building_factpack: 'Preparing your financial data...',
  checking_data: 'Analyzing your situation...',
  generating_journey: 'Creating your personalized journey...',
  validating: 'Validating recommendations...',
  complete: 'Journey ready!',
  error: 'An error occurred',
};

// ============================================
// Hook Implementation
// ============================================

export function useJourney(options: UseJourneyOptions): UseJourneyReturn {
  const { factPack, onSuccess, onError } = options;

  // State
  const [journey, setJourney] = useState<JourneyOutput | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<ProgressStage>('idle');
  const [isFallback, setIsFallback] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]); // Data quality warnings
  const [remainingGenerations, setRemainingGenerations] = useState(3); // Default, updated from server

  // Refs for concurrent request prevention
  const abortControllerRef = useRef<AbortController | null>(null);
  const isGeneratingRef = useRef(false);

  // Progress object
  const progress: JourneyProgress = {
    stage,
    message: PROGRESS_MESSAGES[stage],
  };

  // Generate journey - now requires email parameter
  const generate = useCallback(async (email: string) => {
    // Prevent concurrent requests
    if (isGeneratingRef.current) {
      return;
    }

    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    isGeneratingRef.current = true;
    setIsGenerating(true);
    setError(null);
    setIsFallback(false);

    try {
      // Stage 1: Building fact pack
      setStage('building_factpack');

      // Calculate hash for change detection
      const factPackHash = hashFactPackForCache(factPack as Record<string, unknown>);

      // Check if we can use cached journey
      if (!shouldRegenerate(factPackHash)) {
        const stored = loadJourney();
        if (stored && !stored.journey._isFallback) {
          setJourney(stored.journey);
          setStage('complete');
          setIsGenerating(false);
          isGeneratingRef.current = false;
          onSuccess?.(stored.journey, remainingGenerations);
          return;
        }
      }

      // RULE ENGINE MODE: Skip LLM API entirely, use Rule Engine (fallback) directly
      // Toggle USE_RULE_ENGINE_ONLY in mockJourneyResponse.ts
      if (USE_RULE_ENGINE_ONLY) {

        // ============================================
        // LABOR ILLUSION: Premium "AI analyzing" experience
        // Creates trust & perceived value through staged progress
        // Total duration: ~23 seconds (ensures JourneyLoading reaches 100%)
        //
        // JourneyLoading visual stages timing:
        // - Stages 0-2: 4600ms (profile, RNOR, move date)
        // - Stages 3-6: 8400ms (treaty, capital gains, retirement, liquidation)
        // - Stages 7-9: 5000ms (tax opt, repatriation, compliance)
        // - Stages 10-11: 2000ms (accuracy, finalizing)
        // Total visual: ~20 seconds
        //
        // Research shows users rate results HIGHER when they see
        // work being done (TurboTax, Credit Karma, etc.)
        // ============================================

        // Generate journey using Rule Engine immediately (result ready, but we build anticipation)
        const ruleEngineJourney = generateFallbackJourney(factPack);

        // Stage 1: Building factpack (profile scanning) - 5 seconds
        // Visual: stages 0-2 (profile, RNOR check, move date) = 4600ms
        await new Promise((resolve) => setTimeout(resolve, 5000));

        // Stage 2: Checking data (treaty & capital gains analysis) - 9 seconds
        // Visual: stages 3-6 (treaty, capital gains, retirement, liquidation) = 8400ms
        setStage('checking_data');
        await new Promise((resolve) => setTimeout(resolve, 9000));

        // Stage 3: Generating journey (tax optimization & repatriation) - 5.5 seconds
        // Visual: stages 7-9 (tax optimization, repatriation, compliance) = 5000ms
        setStage('generating_journey');
        await new Promise((resolve) => setTimeout(resolve, 5500));

        // Stage 4: Validating (accuracy review & finalizing) - 3.5 seconds
        // Visual: stages 10-11 (accuracy review, finalizing) = 2000ms + buffer for 100%
        setStage('validating');
        await new Promise((resolve) => setTimeout(resolve, 3500));

        // Total: ~23 seconds of premium "analysis" experience (ensures 100% progress)

        // Capture email in KV for marketing + rate limiting (non-blocking)
        // This ensures emails are stored even when LLM is bypassed
        const deviceId = getOrCreateDeviceId();
        fetch('/api/journey/capture-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.toLowerCase().trim(),
            deviceId,
          }),
        }).catch((err) => {
          console.warn('[useJourney] Email capture failed (non-critical):', err);
        });

        setJourney(ruleEngineJourney);
        setIsFallback(false); // Not a fallback - it's the primary engine!
        saveJourney(ruleEngineJourney, hashFactPackForCache(factPack as Record<string, unknown>));
        setStage('complete');
        setWarnings([]);
        onSuccess?.(ruleEngineJourney, remainingGenerations);
        return;
      }

      // LLM MODE: Standard API flow
      // Stage 2: Checking data
      setStage('checking_data');

      // Small delay for UI feedback
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Stage 3: Generating journey
      setStage('generating_journey');

      // Get device ID for rate limiting
      const deviceId = getOrCreateDeviceId();

      // DEV: Mock response for UI development (toggle USE_MOCK in mockJourneyResponse.ts)
      let result;
      let response = { ok: true, status: 200 };

      if (USE_MOCK) {
        await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate API delay
        result = { ...MOCK_JOURNEY_RESPONSE, rateLimit: { remaining: 2, limit: 3 } };
      } else {
        // Create timeout for frontend (65s - slightly more than Vercel's 60s Hobby limit)
        const timeoutId = setTimeout(() => {
          abortControllerRef.current?.abort();
        }, 65000);

        try {
          const apiResponse = await fetch(API_ENDPOINTS.journey, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              factPack,
              email: email.toLowerCase().trim(),
              deviceId,
            }),
            signal: abortControllerRef.current.signal,
          });

          clearTimeout(timeoutId);
          result = await apiResponse.json();
          response = { ok: apiResponse.ok, status: apiResponse.status };
        } catch (fetchErr) {
          clearTimeout(timeoutId);
          throw fetchErr;
        }
      }

      // Handle rate limiting
      if (response.status === 429) {
        const errorMsg = result.error || 'Rate limit reached. Please try again later.';
        setError(errorMsg);
        setStage('error');
        onError?.(errorMsg);
        return;
      }

      // Handle other errors
      if (!response.ok) {
        throw new Error(result.error || `API error: ${response.status}`);
      }

      // Stage 4: Validating
      setStage('validating');

      if (result.success) {
        // Update remaining from server response
        if (result.rateLimit?.remaining !== undefined) {
          setRemainingGenerations(result.rateLimit.remaining);
        }

        // Capture warnings from API response (data quality issues)
        const apiWarnings: string[] = result.warnings || [];
        // Also check journey._warnings (embedded in journey object)
        const journeyWarnings: string[] = result.journey?._warnings || [];
        const allWarnings = [...apiWarnings, ...journeyWarnings];
        setWarnings(allWarnings);

        // Warnings are captured in state for UI display if needed

        setJourney(result.journey);
        saveJourney(result.journey, factPackHash);
        setStage('complete');
        onSuccess?.(result.journey, result.rateLimit?.remaining ?? remainingGenerations);
      } else {
        throw new Error(result.error || 'Generation failed');
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      const errorMessage =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      console.error('[useJourney] Generation failed:', err);

      setError(errorMessage);
      setStage('error');
      onError?.(errorMessage);

      // Generate fallback journey
      try {
        const fallback = generateFallbackJourney(factPack);
        setJourney(fallback);
        setIsFallback(true);
        saveJourney(fallback, hashFactPackForCache(factPack as Record<string, unknown>));
        // Still call onSuccess with fallback
        onSuccess?.(fallback, remainingGenerations);
      } catch (fallbackErr) {
        console.error('[useJourney] Fallback also failed:', fallbackErr);
      }
    } finally {
      isGeneratingRef.current = false;
      setIsGenerating(false);
    }
  }, [factPack, onSuccess, onError, remainingGenerations]);

  // Clear state
  const clear = useCallback(() => {
    setJourney(null);
    setError(null);
    setStage('idle');
    setIsFallback(false);
    setWarnings([]);
  }, []);

  return {
    // State
    journey,
    isGenerating,
    error,
    progress,
    isFallback,
    warnings,

    // Pre-computed data from Rule Engine (for direct UI rendering)
    preComputedData: factPack.preComputedJourneyData ?? null,

    // Rate limit
    remainingGenerations,

    // Actions
    generate,
    clear,
  };
}
